import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/services/auth-service";
import WhimsicalBackground from "@/components/WhimsicalBackground";

const PasswordResetPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const uid = searchParams.get('uid');
    const { toast } = useToast();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isTokenValid, setIsTokenValid] = useState(true);
    const [isTokenChecked] = useState(true);

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({
        password: '',
        confirmPassword: ''
    });

    const validateForm = () => {
        const newErrors = {
            password: '',
            confirmPassword: ''
        };

        if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters long';
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const numUid = parseInt(uid)
            await api.post('/auth/password/reset/confirm/', {
                "token": token,
                "uid": numUid.toString(36),
                "new_password1": formData.password,
                "new_password2": formData.confirmPassword,
            });

            toast({
                title: "Password reset successful",
                description: "You can now log in with your new password."
            });

            navigate('/signin');
        } catch (error) {
            // @ts-expect-error ignoring
            if (error.status === 400 && error.response.data?.token[0] === "Invalid value") {
                setIsTokenValid(false)
            } else {
                toast({
                    title: "Failed to reset password",
                    // @ts-expect-error ignoring
                    description: error.response?.data?.message || "Please try again later.",
                    variant: "destructive"
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (!isTokenChecked) {
        return (
            <div className="grid min-h-screen place-items-center">
                <WhimsicalBackground />
                <Loader2 className="w-8 h-8 animate-spin" />
            </div>
        );
    }

    if (!isTokenValid) {
        return (
            <div className="grid p-4 place-items-center">
                <WhimsicalBackground />
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CardTitle>Invalid Reset Link</CardTitle>
                        <CardDescription>
                            This password reset link has expired or is invalid.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex justify-center">
                        <Button onClick={() => navigate('/forgot-password')}>
                            Request New Reset Link
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="grid p-4 place-items-center">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle>Reset Your Password</CardTitle>
                    <CardDescription>
                        Please enter your new password below.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">New Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Enter your new password"
                                value={formData.password}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    password: e.target.value
                                }))}
                                disabled={isLoading}
                            />
                            {errors.password && (
                                <p className="text-sm text-destructive">{errors.password}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm Password</Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                placeholder="Confirm your new password"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    confirmPassword: e.target.value
                                }))}
                                disabled={isLoading}
                            />
                            {errors.confirmPassword && (
                                <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Resetting Password
                                </>
                            ) : (
                                "Reset Password"
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default PasswordResetPage;
